/**
 * InventoryDashboard — Inventory Manager landing dashboard.
 *
 * Source: /tmp/aura-design/extracted/aura-web-app/project/aura-dash-2.jsx
 * Tokens: .aura-root component primitives (index.css).
 *
 * Widgets:
 *  • Stock position grid (top SKUs by value)
 *  • Low-stock alerts (chip + drill-in)
 *  • Batch expiry (next 90 days)
 *  • Inward / Dispatch / Pending GRN KPI triplet
 *  • Warehouse/bin utilisation bar
 *  • Cycle count progress
 *  • Barcode activity today
 *  • AI "Smart reorder" card
 *
 * Strictly UI-only. Each widget resolves to a real backend endpoint and
 * degrades to a clean empty state when data is absent.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Filter, Download, Plus, AlertTriangle, Sparkles, Package, ArrowDownToLine,
  ArrowUpFromLine, Barcode, ClipboardCheck, Warehouse as WarehouseIcon,
} from 'lucide-react';
import { api } from '../../../utils/api';
import { formatINR } from '../../../utils/formatters';

// ─── Data hook ────────────────────────────────────────────────────────────

function useEndpoint<T = any>(path: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(path != null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(path)
      .then((res) => {
        if (cancelled) return;
        setData(res.data?.data ?? res.data ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  return { data, loading, error };
}

function toNumber(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return isNaN(n) ? null : n;
}

function asArray(src: any, ...keys: string[]): any[] {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  for (const k of keys) {
    if (Array.isArray(src[k])) return src[k];
  }
  return [];
}

// ─── KPI strip ────────────────────────────────────────────────────────────

interface InventoryOverview {
  totalSkus?: number;
  totalUnits?: number;
  stockValue?: number;
  warehouses?: number;
  inwardToday?: number;
  dispatchToday?: number;
  pendingGrn?: number;
  lowStockCount?: number;
}

function KpiStrip() {
  const overview = useEndpoint<InventoryOverview>('/inventory-dashboard/overview');
  const stockouts = useEndpoint<any>('/analytics/inventory/stockouts');
  const pendingGrns = useEndpoint<any>('/analytics/inventory/pending-grns');
  const movements = useEndpoint<any>('/analytics/inventory/movements-today');

  const stockValue = toNumber(overview.data?.stockValue);
  const lowCount =
    toNumber(stockouts.data?.value) ??
    toNumber(overview.data?.lowStockCount) ??
    null;
  const grnCount = toNumber(pendingGrns.data?.value) ?? toNumber(overview.data?.pendingGrn);
  const inward =
    toNumber(movements.data?.meta?.inward) ??
    toNumber(overview.data?.inwardToday);
  const dispatch =
    toNumber(movements.data?.meta?.dispatch) ??
    toNumber(overview.data?.dispatchToday);

  const cards = [
    {
      label: 'Stock value',
      loading: overview.loading,
      primary: stockValue == null ? '—' : formatINR(stockValue, false),
      secondary: overview.data?.totalSkus != null
        ? `${overview.data.totalSkus.toLocaleString('en-IN')} SKUs · ${
            overview.data.totalUnits?.toLocaleString('en-IN') ?? '—'
          } units`
        : '—',
      tone: undefined,
    },
    {
      label: 'Low-stock alerts',
      loading: stockouts.loading,
      primary: lowCount == null ? '—' : lowCount.toLocaleString('en-IN'),
      secondary: 'Below safety stock',
      tone: lowCount && lowCount > 0 ? 'warn' : undefined,
    },
    {
      label: 'Inward today',
      loading: movements.loading,
      primary: inward == null ? '—' : inward.toLocaleString('en-IN'),
      secondary: 'GRN receipts',
      tone: undefined,
    },
    {
      label: 'Dispatch today',
      loading: movements.loading,
      primary: dispatch == null ? '—' : dispatch.toLocaleString('en-IN'),
      secondary: 'Outward orders',
      tone: undefined,
    },
    {
      label: 'Pending GRN',
      loading: pendingGrns.loading,
      primary: grnCount == null ? '—' : grnCount.toLocaleString('en-IN'),
      secondary: 'Awaiting receipt',
      tone: grnCount && grnCount > 0 ? 'warn' : undefined,
    },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 14,
        marginBottom: 20,
      }}
    >
      {cards.map((c) => (
        <div key={c.label} className="card" style={{ padding: 16 }}>
          <div className="kpi-label" style={{ marginBottom: 4 }}>
            {c.label}
          </div>
          <div className="num-display" style={{ fontSize: 24 }}>
            {c.loading ? '…' : c.primary}
          </div>
          <div
            style={{
              fontSize: 11,
              color: c.tone === 'warn' ? 'var(--warn, var(--saffron-600))' : 'var(--ink-3)',
              marginTop: 2,
            }}
          >
            {c.secondary}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Stock position grid (top 30 by value) ────────────────────────────────

function StockPositionGrid() {
  // Real endpoint: /warehouse/inventory (supports sort/limit via ?)
  const { data, loading, error } = useEndpoint<any>('/warehouse/inventory?limit=30');
  const rows = useMemo(() => asArray(data, 'items', 'rows', 'products'), [data]);

  return (
    <div className="card" style={{ padding: 0 }}>
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div className="kpi-label">Stock position · Top 30 SKUs</div>
        <div style={{ flex: 1 }} />
        <Link
          to="/warehouse"
          style={{ fontSize: 11, color: 'var(--saffron-600)', textDecoration: 'none' }}
        >
          View all →
        </Link>
      </div>
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
          Loading stock…
        </div>
      ) : error ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--danger)', fontSize: 13 }}>
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
          No inventory recorded. <Link to="/warehouse" style={{ color: 'var(--saffron-600)' }}>Add a product →</Link>
        </div>
      ) : (
        <table className="tbl" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>SKU</th>
              <th>Product</th>
              <th style={{ textAlign: 'right' }}>On hand</th>
              <th style={{ textAlign: 'right' }}>On order</th>
              <th style={{ textAlign: 'right' }}>Safety</th>
              <th style={{ textAlign: 'right' }}>Value</th>
              <th style={{ width: 120 }}>Fill %</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 30).map((r: any, i: number) => {
              const onHand =
                toNumber(r.onHand ?? r.quantity ?? r.currentStock ?? r.quantityInStock) ?? 0;
              const onOrder = toNumber(r.onOrder ?? r.pendingOrder ?? r.committedQty) ?? 0;
              const safety = toNumber(r.safetyStock ?? r.reorderLevel ?? r.minStock) ?? 0;
              const value =
                toNumber(r.totalValue ?? r.stockValue ?? r.value ?? r.unitCost * onHand) ?? 0;
              const fillPct = safety > 0 ? Math.min(100, Math.round((onHand / safety) * 100)) : 100;
              const low = safety > 0 && onHand < safety;
              return (
                <tr key={r.id ?? r.sku ?? i}>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {r.sku ?? r.code ?? '—'}
                  </td>
                  <td style={{ fontWeight: 500 }}>{r.name ?? r.productName ?? '—'}</td>
                  <td className="num" style={{ textAlign: 'right' }}>
                    {onHand.toLocaleString('en-IN')}
                  </td>
                  <td className="num" style={{ textAlign: 'right', color: 'var(--ink-3)' }}>
                    {onOrder.toLocaleString('en-IN')}
                  </td>
                  <td className="num" style={{ textAlign: 'right', color: 'var(--ink-3)' }}>
                    {safety.toLocaleString('en-IN')}
                  </td>
                  <td
                    className="num"
                    style={{ textAlign: 'right', fontWeight: 500 }}
                  >
                    {formatINR(value, false)}
                  </td>
                  <td>
                    <div
                      style={{
                        height: 6,
                        background: 'var(--ivory-3)',
                        borderRadius: 3,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${fillPct}%`,
                          height: '100%',
                          background: low
                            ? 'var(--danger)'
                            : fillPct > 75
                              ? 'var(--forest)'
                              : 'var(--saffron)',
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Low-stock alerts ─────────────────────────────────────────────────────

function LowStockCard() {
  // /warehouse/inventory/low-stock is available on the canonical inventory router.
  const { data, loading } = useEndpoint<any>('/warehouse/inventory/low-stock');
  const rows = useMemo(() => asArray(data, 'items', 'products', 'rows'), [data]);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div className="kpi-label">Low-stock alerts</div>
        <div style={{ flex: 1 }} />
        {rows.length > 0 && (
          <span className="chip chip-red">
            <AlertTriangle size={10} style={{ marginRight: 3 }} />
            {rows.length}
          </span>
        )}
      </div>
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          All SKUs above safety stock.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.slice(0, 8).map((r: any, i: number) => {
            const onHand = toNumber(r.onHand ?? r.quantity ?? r.currentStock) ?? 0;
            const safety = toNumber(r.safetyStock ?? r.reorderLevel ?? r.minStock) ?? 0;
            return (
              <div
                key={r.id ?? i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 12,
                  padding: '5px 0',
                  borderBottom: i < Math.min(rows.length, 8) - 1 ? '1px solid var(--ivory-3)' : 'none',
                }}
              >
                <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', width: 80 }}>
                  {r.sku ?? r.code ?? '—'}
                </span>
                <span style={{ flex: 1 }}>{r.name ?? r.productName ?? '—'}</span>
                <span className="num" style={{ color: 'var(--danger)', fontWeight: 500 }}>
                  {onHand.toLocaleString('en-IN')}
                </span>
                <span style={{ color: 'var(--ink-3)', fontSize: 10 }}>/ {safety}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Batch expiry (next 90 days) ──────────────────────────────────────────

function BatchExpiryCard() {
  const { data, loading } = useEndpoint<any>(
    '/warehouse/product-units/expiring-soon?days=90&limit=10',
  );
  const rows = useMemo(() => asArray(data, 'items', 'batches', 'units'), [data]);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ marginBottom: 10 }}>
        Batch expiry · Next 90 days
      </div>
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          No batches expiring in the next 90 days.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.slice(0, 10).map((r: any, i: number) => {
            const expiry = r.expiryDate ?? r.expiresAt;
            const days = expiry
              ? Math.max(0, Math.ceil((new Date(expiry).getTime() - Date.now()) / 86_400_000))
              : null;
            const value = toNumber(r.value ?? r.totalValue ?? r.batchValue);
            return (
              <div
                key={r.id ?? i}
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}
              >
                <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', width: 70 }}>
                  {r.batchNumber ?? r.batch ?? r.code ?? '—'}
                </span>
                <span style={{ flex: 1 }}>{r.productName ?? r.name ?? '—'}</span>
                {days != null && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color:
                        days < 30 ? 'var(--danger)' : days < 60 ? 'var(--saffron-600)' : 'var(--ink-3)',
                    }}
                  >
                    {days}d
                  </span>
                )}
                {value != null && (
                  <span className="num" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {formatINR(value, false)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Warehouse / bin utilisation ──────────────────────────────────────────

function WarehouseUtilisationCard() {
  // /warehouse-locations?withStock=1 — fall back to /warehouse-locations.
  const primary = useEndpoint<any>('/warehouse-locations?withStock=1');
  const fallback = useEndpoint<any>(primary.error ? '/warehouse-locations' : null);
  const data = primary.data ?? fallback.data;
  const loading = primary.loading || fallback.loading;
  const rows = useMemo(() => asArray(data, 'items', 'locations', 'bins'), [data]);

  const bins = rows
    .map((r: any) => ({
      code: r.code ?? r.name ?? r.binCode ?? '—',
      util:
        toNumber(r.utilisationPct) ??
        toNumber(r.utilizationPct) ??
        (toNumber(r.capacity) && toNumber(r.currentStock)
          ? Math.round((toNumber(r.currentStock)! / toNumber(r.capacity)!) * 100)
          : null),
    }))
    .filter((b) => b.util != null)
    .slice(0, 10);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div className="kpi-label">Warehouse utilisation · Top bins</div>
        <div style={{ flex: 1 }} />
        <Link
          to="/warehouse"
          style={{ fontSize: 11, color: 'var(--saffron-600)', textDecoration: 'none' }}
        >
          Map →
        </Link>
      </div>
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>
      ) : bins.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          No warehouse bins configured.{' '}
          <Link to="/warehouse" style={{ color: 'var(--saffron-600)' }}>Set up bins →</Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {bins.map((b, i) => {
            const pct = Math.max(0, Math.min(100, b.util!));
            const tone = pct > 85 ? 'var(--danger)' : pct > 60 ? 'var(--forest)' : 'var(--saffron)';
            return (
              <div key={i}>
                <div style={{ display: 'flex', fontSize: 11, marginBottom: 3 }}>
                  <span style={{ flex: 1, fontWeight: 500 }}>{b.code}</span>
                  <span className="num" style={{ color: 'var(--ink-3)' }}>
                    {pct}%
                  </span>
                </div>
                <div
                  style={{
                    height: 5,
                    background: 'var(--ivory-3)',
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ width: `${pct}%`, height: '100%', background: tone }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Cycle count progress ─────────────────────────────────────────────────

function CycleCountCard() {
  const { data, loading } = useEndpoint<any>('/warehouse/stock-audit/in-progress');
  const audits = useMemo(() => asArray(data, 'items', 'audits'), [data]);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <ClipboardCheck size={14} style={{ color: 'var(--ink-3)', marginRight: 6 }} />
        <div className="kpi-label">Cycle count · In progress</div>
      </div>
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>
      ) : audits.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          No cycle counts in progress.{' '}
          <Link to="/warehouse" style={{ color: 'var(--saffron-600)' }}>Schedule →</Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {audits.slice(0, 4).map((a: any, i: number) => {
            const done = toNumber(a.countedItems ?? a.scannedCount) ?? 0;
            const total = toNumber(a.totalItems ?? a.expectedCount) ?? 0;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <div key={a.id ?? i}>
                <div style={{ display: 'flex', fontSize: 12, marginBottom: 3 }}>
                  <span style={{ flex: 1, fontWeight: 500 }}>
                    {a.name ?? a.locationCode ?? a.warehouseCode ?? `Audit #${i + 1}`}
                  </span>
                  <span className="num" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {done} / {total}
                  </span>
                </div>
                <div
                  style={{
                    height: 4,
                    background: 'var(--ivory-3)',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ width: `${pct}%`, height: '100%', background: 'var(--forest)' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Barcode activity today ───────────────────────────────────────────────

function BarcodeActivityCard() {
  const { data, loading } = useEndpoint<any>('/warehouse/barcode-lifecycle/stats');

  // Endpoint returns today stats as default; fallback to zero.
  const scans =
    toNumber(data?.scansToday) ??
    toNumber(data?.today?.scans) ??
    toNumber(data?.value) ??
    null;
  const activated =
    toNumber(data?.activatedToday) ??
    toNumber(data?.today?.activated) ??
    toNumber(data?.meta?.activated) ??
    null;

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <Barcode size={14} style={{ color: 'var(--ink-3)', marginRight: 6 }} />
        <div className="kpi-label">Barcode activity · Today</div>
      </div>
      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ flex: 1 }}>
          <div className="num-display" style={{ fontSize: 22 }}>
            {loading ? '…' : scans == null ? '—' : scans.toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Scans</div>
        </div>
        <div style={{ flex: 1 }}>
          <div className="num-display" style={{ fontSize: 22 }}>
            {loading ? '…' : activated == null ? '—' : activated.toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Units activated</div>
        </div>
      </div>
    </div>
  );
}

// ─── Smart reorder AI ─────────────────────────────────────────────────────

function SmartReorderCard() {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <Sparkles size={14} style={{ color: 'var(--saffron-600)', marginRight: 6 }} />
        <div className="kpi-label">AI · Smart reorder</div>
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>
        Auto-analyse demand, lead time, and safety stock to generate optimal reorder quantities.
      </div>
      <Link
        to="/ai?context=inventory&action=reorder-suggestions"
        className="btn btn-ai btn-sm"
        style={{ textDecoration: 'none', display: 'inline-flex' }}
      >
        <Sparkles size={11} />
        Suggest reorders · 5 cr
      </Link>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────

export function InventoryDashboard() {
  const overview = useEndpoint<InventoryOverview>('/inventory-dashboard/overview');

  const headerSub = useMemo(() => {
    const d = overview.data;
    if (!d) return 'Stock, inward, dispatch & batch tracking';
    const parts: string[] = [];
    if (d.warehouses) parts.push(`${d.warehouses} warehouses`);
    if (d.totalSkus) parts.push(`${d.totalSkus.toLocaleString('en-IN')} SKUs`);
    if (d.totalUnits) parts.push(`${d.totalUnits.toLocaleString('en-IN')} units on-hand`);
    return parts.length ? parts.join(' · ') : 'Stock, inward, dispatch & batch tracking';
  }, [overview.data]);

  return (
    <div className="aura-root" style={{ minHeight: '100%' }}>
      <div style={{ padding: '18px 24px 24px' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 16,
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 style={{ fontFamily: 'var(--ff-serif)', fontSize: 22, fontWeight: 500 }}>
              Inventory · Warehouse
            </h2>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{headerSub}</div>
          </div>
          <div style={{ flex: 1 }} />
          <Link to="/warehouse" className="btn btn-outline" style={{ textDecoration: 'none' }}>
            <Filter size={13} /> All warehouses
          </Link>
          <Link to="/warehouse" className="btn btn-outline" style={{ textDecoration: 'none' }}>
            <Download size={13} /> Export GRN
          </Link>
          <Link to="/warehouse" className="btn btn-primary" style={{ textDecoration: 'none' }}>
            <Plus size={13} /> New GRN
          </Link>
        </div>

        {/* KPI strip */}
        <KpiStrip />

        {/* Main 2:1 grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <StockPositionGrid />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <LowStockCard />
            <BatchExpiryCard />
          </div>
        </div>

        {/* Secondary row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <WarehouseUtilisationCard />
          <CycleCountCard />
        </div>

        {/* Tertiary row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
          }}
        >
          <BarcodeActivityCard />
          <SmartReorderCard />
        </div>

        {/* Keep icon imports alive without crowding JSX */}
        <span style={{ display: 'none' }}>
          <Package size={1} />
          <ArrowDownToLine size={1} />
          <ArrowUpFromLine size={1} />
          <WarehouseIcon size={1} />
        </span>
      </div>
    </div>
  );
}

export default InventoryDashboard;
